export interface LLMFeatureMetadata {
  name: string;
  overview: string;
}

/**
 * Human-readable names and descriptions for each LLM feature config key.
 * Used in both the Tenant Admin Console (AI Settings) and the internal AI Enablement tool.
 */
export const LLM_FEATURE_METADATA: Record<string, LLMFeatureMetadata> = {
  LLM_ENABLE_AGENT_APPROVALS: {
    name: 'Full actions mode',
    overview: 'Enable Full actions mode so Chat can create and edit within existing user permissions.',
  },
  ELN_ENABLE_AI_CHECKER: {
    name: 'Notebook Check',
    overview:
      "Let Notebook Check look over your Notebook entry to check for mistakes and adherence to your team's guidelines.",
  },
  INSIGHTS_ENABLE_AI_SQL_ASSISTANT: {
    name: 'SQL Writer',
    overview:
      'Write part of a SQL query in an Insights dashboard, and let SQL Writer fill in the rest of the details. We recommend that users have some SQL experience or are interested in learning.',
  },
  ELN_ENABLE_AI_DATA_ENTRY: {
    name: 'Data Entry',
    overview:
      'Provide any data files or prompt, and let Data Entry fill the details into your Notebook table.',
  },
  LLM_ENABLE_AI_DEEP_RESEARCH: {
    name: 'Deep Research Agent',
    overview: 'Ask questions about data stored in Benchling. Generate reports including linked citations.',
  },
  LLM_ENABLE_AI_DATA_CAPTURE_ASSISTANT: {
    name: 'Compose Agent',
    overview:
      'Create a draft of any notebook entry or template, including protocol extraction, data capture, experiment design, and more.',
  },
  LLM_ENABLE_REALTIME_VOICE_IN_COMPOSE: {
    name: 'Realtime voice',
    overview:
      'Enable Voice conversations with Chat for real-time, hands-free data capture. Chat must be enabled to use this feature.',
  },
  LLM_ENABLE_CO_SCIENTIST: {
    name: 'AI Scientist',
    overview: 'Enable the AI Scientist agent mode to manage deep and long-running scientific work.',
  },
  LLM_ENABLE_AGENT_FACTORIES: {
    name: 'Foundries',
    overview:
      'Enable Agent Foundries, which track and coordinate multi-step scientific work run by a team of agents.',
  },
  LLM_ENABLE_ROUTINES_RUN_AS_SERVICE_ACCOUNT: {
    name: 'Agents with their own permissions',
    overview: 'Allow configuring agents to be run with their own provisioned identity.',
  },
  LLM_ENABLE_AI_DEEP_RESEARCH_PUBLIC_DATA: {
    name: 'Web Search',
    overview:
      'Let agents use web search to answer questions. By default, Benchling uses AWS AgentCore Web Search with zero egress. Enable this setting to use additional web search providers for better performance. Chat or Deep Research must be enabled to use this feature.',
  },
  SEARCH_ENABLE_AI_ASSISTED_SEARCH: {
    name: 'Guided Search',
    overview:
      'Describe your search in plain language, and let Guided Search suggest the right search filters and settings.',
  },
  ELN_ENABLE_AI_TOOLS: {
    name: 'Notebook writer',
    overview:
      'Let notebook writer summarize, draft content, and answer questions about your Notebook entry and related information.',
  },
  INVENTORY_ENABLE_AI_PLATE_MAP_ANNOTATION_ASSISTANT: {
    name: 'Plate Annotator',
    overview: 'Describe well role in text and have the Plate Annotator assign roles to wells.',
  },
  ELN_ENABLE_AI_DOCUMENT_GENERATION: {
    name: 'Notebook document generation',
    overview: 'Generate notebook documents from uploaded files.',
  },
  LLM_ENABLE_LOOKUP_TABLES_IN_COMPOSE: {
    name: 'Chat with lookup tables',
    overview: 'Enable lookup table access within chat.',
  },
  LLM_ENABLE_AI_CUSTOM_CODE_IN_PIPELINES: {
    name: 'AI custom code in pipelines',
    overview: 'Generate and run custom code steps within analysis pipelines using AI.',
  },
  LLM_ENABLE_PIPELINES_CUSTOM_CODE_WRITER_AGENT: {
    name: 'Custom Code Writer',
    overview: 'Write and edit Benchling Custom Code in analysis pipelines using AI.',
  },
  ANALYSIS_TOOL_ENABLE_AI_DESCRIPTION_WRITER: {
    name: 'Pipeline Description Writer',
    overview: 'Generate a plain-English description of an analysis based on its pipeline steps.',
  },
  LLM_DATA_ACCESS_CONSENT: {
    name: 'Benchling data access and use',
    overview:
      'Allow Benchling to access input/output data from AI feature usage to debug issues and evaluate performance.',
  },
  LLM_ENABLE_ASSISTANT_AGENT: {
    name: 'Chat',
    overview:
      'The new and improved Benchling AI experience. Ask questions, generate cited reports, draft notebook entries and templates, and analyze your data — all in one agent.',
  },
  MODEL_HUB_SCIENTIFIC_AI_ENABLEMENT_STATE: {
    name: 'Scientific AI Models',
    overview:
      'Run AI-powered structure prediction and generative models for protein engineering and molecular design.',
  },
  MODEL_HUB_TENANT_ADMIN_CAN_INSTALL_MODELS: {
    name: 'Model Installation by Tenant Admins',
    overview:
      'When enabled, a Benchling admin can toggle the config on or off that lets tenant admins install and uninstall scientific AI models. Not changeable by customer; Benchling admin must flip the flag, then flip this back to off to prevent future changes.',
  },
  INSIGHTS_ENABLE_EXPERIMENT_OPTIMIZATION_WITH_ADVANCED_ANALYSIS_AND_INSIGHTS_LABS: {
    name: 'Enable Experiment Optimization',
    overview:
      'When enabled, a Benchling admin can toggle the config on or off that enables Experiment Optimization when Advanced Analysis and Insights Labs are also enabled. Not changeable by customer; Benchling admin must flip the flag.',
  },
};
